import { useQuery } from '@apollo/client';
import { Box, styled } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import PageContainer from 'Components/PageContainer';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ComponentItemTable from 'FeatureModules/ComponentChain/Components/ComponentItemsTable';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { EComponentItemTableColumnKeys } from 'FeatureModules/ComponentChain/utils/headerColumns';
import usePartnerComponentItems from 'FeatureModules/Partner/hooks/usePartnerComponentItems';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import ProductSummarySkeleton from 'FeatureModules/ProductPage/Components/Tables/ProductSummarySkeleton';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import useDialog from 'hooks/useDialog';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TRouteParamsWithId } from 'types/router.types';

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const PartnerComponentItems: FC = () => {
  const { openDialog } = useDialog();
  const {
    components,
    error,
    loading,
    searchTerm,
    filters,
    handleSearchTermChange,
    handleFilterChange,
    handleFilterReset,
  } = usePartnerComponentItems();
  const navigate = useNavigate();
  const { id: companyId } = useParams<TRouteParamsWithId>();

  const { data } = useQuery<{ partner: IBasePartnerCompany }>(GET_BASE_PARTNER_COMPANY, {
    variables: { id: companyId },
    skip: !companyId,
  });

  const handleCreateComponent = () => {
    openDialog({
      type: 'ADD_EDIT_COMPONENT',
      props: {
        title: 'Add new partner component',
        hidePartner: true,
        partner: data?.partner,
        infoText:
          'A product coming from this partner. This can be a component of your end products, such as an ingredient.',
        onComponentCreated: componentId => navigate(`${componentId}`),
      },
    });
  };

  const handleRowClick = ({ id }: IComponentItem) => navigate(`/components/${id}`);

  if (loading) return <Loader />;
  if (error) return <ErrorState />;

  if (!components.length && searchTerm.length === 0 && !filters.some(({ value }) => !!value)) {
    return (
      <Box mt={4}>
        <EmptyStateCard
          title="Add your product components to start tracing the material flow of your supply chains"
          onCreateItemClick={handleCreateComponent}
          variant={EItemTypes.COMPONENT}
          buttonText="Add component"
          background={EBackgroundTypes.WORLD}
          renderIllustration={
            <CardIllustration image={EIllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
          size={EAvailableSizes.LARGE}
          data-testid="components-getting-started-card"
          data-testid-button="components-getting-started-card-button"
        />
      </Box>
    );
  }

  return (
    <PageContainer>
      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <Container>
        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm || ''}
            className="search-input"
            data-testid="components-search-input"
            placeholder="Search component"
            actions={
              <Box ml={1}>
                <ThemeButton
                  size="medium"
                  color="PRIMARY"
                  onClick={handleCreateComponent}
                  startIcon={<Plus size={20} color="white" />}
                  data-testid="add-component-btn"
                >
                  Add new component
                </ThemeButton>
              </Box>
            }
          />
        </ActionContainer>

        <ScrollableTableContainer pb={1}>
          {!!components?.length && (
            <ComponentItemTable
              data-testid="component-table"
              components={components}
              columns={[
                EComponentItemTableColumnKeys.TITLE,
                EComponentItemTableColumnKeys.MODIFIED_TIMESTAMP,
                EComponentItemTableColumnKeys.ACTIONS,
              ]}
              onSelect={handleRowClick}
              hidePartner={true}
            />
          )}
        </ScrollableTableContainer>
      </Container>

      {/* No result container */}
      {!components?.length && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Add component',
            onClick: handleCreateComponent,
          }}
          skeletonComponent={<ProductSummarySkeleton title={searchTerm} />}
        />
      )}
    </PageContainer>
  );
};

export default PartnerComponentItems;
